// SP6 Task 4 — derives the shared ExpressionEditor's picker options
// (collections.check/coupon/allocation, transactionRefs, codeSuggestions)
// from the draft's JobTypeVersionContentV3. Pure function — no store access,
// so every ExpressionEditor consumer in later SP6 tasks (BalancePolicy,
// TransactionRules, TransactionFields, Allocation) computes its own pickerData
// via `buildPickerData(contentV3)` off the same draft the editor is bound to.
import type { JobTypeVersionContentV3 } from '@capture/shared';
import { DEFAULT_PICKER_DATA, type ExpressionPickerData } from '../shared/ExpressionEditor';


/** Field names (internalName ?? name) across every form with the given
 *  remittanceRole, deduped and sorted — for `field.<name>` picker options. */
function fieldNamesForRole(v3: JobTypeVersionContentV3, role: 'payment' | 'coupon'): string[] {
  const names = new Set<string>();
  for (const form of v3.forms ?? []) {
    if (form.remittanceRole !== role) continue;
    for (const field of form.fields ?? []) {
      const name = field.internalName ?? field.name;
      if (name) names.add(name);
    }
  }
  return [...names].sort().map((name) => `field.${name}`);
}

export function buildPickerData(v3: JobTypeVersionContentV3): ExpressionPickerData {
  return {
    // Built-ins spread from the editor's DEFAULT_PICKER_DATA — one source, so
    // a new built-in can't drift between the no-draft fallback and this
    // draft-derived picker. (Spread, not aliased: the shared constant must
    // never be handed out mutably.)
    collections: {
      check: [...DEFAULT_PICKER_DATA.collections.check, ...fieldNamesForRole(v3, 'payment')],
      coupon: [...DEFAULT_PICKER_DATA.collections.coupon, ...fieldNamesForRole(v3, 'coupon')],
      allocation: [...DEFAULT_PICKER_DATA.collections.allocation],
    },
    transactionRefs: [
      ...DEFAULT_PICKER_DATA.transactionRefs,
      ...(v3.remittance?.transactionFields ?? []).map((promotion) => `transaction.${promotion.name}`),
    ],
    codeSuggestions: (v3.remittance?.allocation?.targets ?? [])
      .map((target) => target.code)
      .filter((code): code is string => !!code),
  };
}
